import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Database, Eraser, Loader2, ShieldOff, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  DATA_CLASSES,
  listRetentionPolicies,
  previewPurge,
  runPurgeNow,
  upsertRetentionPolicy,
} from "@/lib/retention.functions";

export const Route = createFileRoute("/_authenticated/app/settings/retention")({
  head: () => ({
    meta: [
      { title: "Data retention — CairnOS" },
      {
        name: "description",
        content:
          "Configure how long each data class is retained and run on-demand purges for GDPR / CCPA compliance.",
      },
    ],
  }),
  component: RetentionPage,
});

function RetentionPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();

  const listFn = useServerFn(listRetentionPolicies);
  const upsertFn = useServerFn(upsertRetentionPolicy);
  const purgeFn = useServerFn(runPurgeNow);

  const q = useQuery({
    queryKey: ["retention-policies", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const byClass = useMemo(() => {
    const m = new Map<string, any>();
    for (const r of q.data?.rows ?? []) m.set(r.data_class, r);
    return m;
  }, [q.data]);

  const [purgeTarget, setPurgeTarget] = useState<string | null>(null);

  const upsertMut = useMutation({
    mutationFn: (p: { dataClass: string; retentionDays: number; enabled: boolean; redactFields: string[] }) =>
      upsertFn({
        data: {
          tenantId: tenantId!,
          dataClass: p.dataClass,
          retentionDays: p.retentionDays,
          enabled: p.enabled,
          redactFields: p.redactFields,
        },
      }),
    onSuccess: () => {
      toast.success("Retention policy saved");
      void qc.invalidateQueries({ queryKey: ["retention-policies", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const purgeMut = useMutation({
    mutationFn: (dataClass: string) =>
      purgeFn({ data: { tenantId: tenantId!, dataClass } }),
    onSuccess: (res, dc) => {
      toast.success(`Removed ${res.removed.toLocaleString()} ${dc} rows older than ${new Date(res.cutoff).toLocaleDateString()}`);
      setPurgeTarget(null);
      void qc.invalidateQueries({ queryKey: ["retention-policies", tenantId] });
    },
    onError: (e) => {
      toast.error((e as Error).message);
      setPurgeTarget(null);
    },
  });

  const configured = (q.data?.rows ?? []).length;
  const enabledCount = (q.data?.rows ?? []).filter((r: any) => r.enabled).length;

  return (
    <div className="p-6 space-y-6 max-w-7xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Database className="h-6 w-6" /> Data retention
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Define maximum retention windows per data class. The scheduled purger runs nightly; you
          can also trigger immediate purges below. Required for GDPR / CCPA Article 17 ("right to
          erasure") and SOC 2 data lifecycle controls.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Data classes" value={DATA_CLASSES.length} sub="available to configure" />
        <StatCard label="Configured" value={configured} sub={`${enabledCount} actively enforced`} accent />
        <StatCard
          label="Unprotected"
          value={DATA_CLASSES.length - configured}
          sub="no retention policy"
          warn
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {DATA_CLASSES.map((dc) => (
          <PolicyCard
            key={dc.id}
            meta={dc}
            policy={byClass.get(dc.id)}
            tenantId={tenantId}
            onSave={(p) => upsertMut.mutate({ dataClass: dc.id, ...p })}
            saving={upsertMut.isPending}
            onRequestPurge={() => setPurgeTarget(dc.id)}
          />
        ))}
      </div>

      <Dialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eraser className="h-5 w-5 text-destructive" /> Purge now
            </DialogTitle>
            <DialogDescription>
              This permanently deletes <span className="font-mono">{purgeTarget}</span> rows older
              than the configured retention window. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPurgeTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={purgeMut.isPending}
              onClick={() => purgeTarget && purgeMut.mutate(purgeTarget)}
            >
              {purgeMut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Trash2 className="h-4 w-4 mr-2" /> Permanently delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({
  label, value, sub, accent, warn,
}: { label: string; value: number; sub: string; accent?: boolean; warn?: boolean }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        <div
          className={`text-2xl font-semibold mt-1 ${
            accent ? "text-primary" : warn && value > 0 ? "text-amber-500" : ""
          }`}
        >
          {value}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>
      </CardContent>
    </Card>
  );
}

function PolicyCard({
  meta, policy, tenantId, onSave, saving, onRequestPurge,
}: {
  meta: (typeof DATA_CLASSES)[number];
  policy: any | undefined;
  tenantId: string | null;
  onSave: (p: { retentionDays: number; enabled: boolean; redactFields: string[] }) => void;
  saving: boolean;
  onRequestPurge: () => void;
}) {
  const [days, setDays] = useState<number>(policy?.retention_days ?? meta.defaultDays);
  const [enabled, setEnabled] = useState<boolean>(policy?.enabled ?? false);
  const [redact, setRedact] = useState<string[]>(policy?.redact_fields ?? []);
  const previewFn = useServerFn(previewPurge);
  const [preview, setPreview] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const dirty =
    !policy ||
    days !== policy.retention_days ||
    enabled !== policy.enabled ||
    JSON.stringify(redact) !== JSON.stringify(policy.redact_fields ?? []);

  const toggleRedact = (f: string) =>
    setRedact((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  const runPreview = async () => {
    if (!tenantId) return;
    setPreviewing(true);
    try {
      const res = await previewFn({
        data: { tenantId, dataClass: meta.id, retentionDays: days },
      });
      setPreview(res.eligibleCount);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPreviewing(false);
    }
  };

  const meetsRecommendation = days >= meta.recommendedDays;

  return (
    <Card className={!enabled && policy ? "opacity-80" : ""}>
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              {meta.label}
              {!policy && (
                <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30">
                  <ShieldOff className="h-3 w-3 mr-1" /> unprotected
                </Badge>
              )}
              {policy?.enabled && (
                <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                  enforced
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="text-xs mt-1">{meta.description}</CardDescription>
          </div>
          <div className="text-[10px] text-muted-foreground font-mono whitespace-nowrap">
            {meta.table}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 items-end">
          <div>
            <Label htmlFor={`days-${meta.id}`}>Retention (days)</Label>
            <Input
              id={`days-${meta.id}`}
              type="number"
              min={1}
              max={3650}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Recommended: <span className="font-mono">{meta.recommendedDays}d</span>
              {!meetsRecommendation && (
                <span className="text-amber-500 ml-1">— shorter than recommended</span>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between rounded border p-2">
            <div>
              <div className="text-xs font-medium">Enabled</div>
              <div className="text-[10px] text-muted-foreground">
                Nightly purger applies this policy
              </div>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>
        </div>

        {meta.redactable.length > 0 && (
          <div>
            <Label className="text-xs">Redact instead of delete</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {meta.redactable.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => toggleRedact(f)}
                  className={`text-[10px] px-2 py-0.5 rounded border font-mono transition ${
                    redact.includes(f)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "hover:bg-muted"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Selected fields are nulled out on rows past retention. Other fields keep their values.
            </div>
          </div>
        )}

        <div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
          {policy?.last_purge_at ? (
            <span>
              Last purge: {formatDistanceToNow(new Date(policy.last_purge_at), { addSuffix: true })}
              {typeof policy.last_purge_count === "number" && (
                <span className="ml-1">({policy.last_purge_count.toLocaleString()} rows)</span>
              )}
            </span>
          ) : (
            <span>No purges yet</span>
          )}
          {policy?.updated_at && (
            <span>
              Updated {formatDistanceToNow(new Date(policy.updated_at), { addSuffix: true })}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={runPreview} disabled={previewing || !tenantId}>
              {previewing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
              Preview eligible
            </Button>
            {preview !== null && (
              <span className="text-xs text-muted-foreground">
                <span className="font-mono">{preview.toLocaleString()}</span> rows older than {days}d
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {policy?.enabled && (
              <Button size="sm" variant="ghost" onClick={onRequestPurge}>
                <Eraser className="h-3.5 w-3.5 mr-1" /> Purge now
              </Button>
            )}
            <Button
              size="sm"
              disabled={!dirty || saving}
              onClick={() => onSave({ retentionDays: days, enabled, redactFields: redact })}
            >
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {policy ? "Save" : "Configure"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}